import React, { useEffect, useMemo } from 'react';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';
import { usePetProfileUpdateContext } from '../context';
import {
  RECIPE_INFO,
  PENSION_INFO,
  getCycleInfo,
  ACTIVITY_INFO,
  BODY_INFO,
  SNACK_LEVEL_INFO,
} from '../types';
import { Recipe, Pension, Cycle } from '../../../core/types';
import { useSelector } from 'react-redux';
import {
  getPetSelector,
  getLineItemSelector,
  getSubscriptionLevelDiscountsSelector,
  getCurrencyCodeSelector,
  getDefaultSubscriptionSelector,
} from '../../../core/selectors';
import { getLastOrderSelector } from '@modules/orders/core/selectors';
import { toAbsoluteUrl, usePossessiveName } from '@helpers/index';
import { FormattedMessage, useIntl } from 'react-intl';
import { KTSVG } from '@shared';
import { getDisabledRecipes } from '../../../utils/getDisabledRecipes';
import { formatCycleInterval } from '../../../utils/formatCycle';
import { isHiddenRecipe } from '@constants/subscription';
import { findMatchingVariant, hasMatchingVariant, isVariantExplicitlyNotForSale } from '../utils';
import { formatMoney } from '@helpers/index';
import {
  CheckIcon,
  LoadingSpinner,
  PriceIcon,
  CheckCircleIcon,
  ScaleIcon,
  LightningIcon,
  HeartIcon,
  FireIcon,
  PackageIcon,
  ArrowLeftIcon,
  CheckmarkIcon,
  ErrorIcon,
  WarningIcon,
} from '../icons';

interface ChangesSummaryProps {
  weightChanged: boolean;
  activityChanged: boolean;
  bodyChanged: boolean;
  snackLevelChanged: boolean;
  caloriesChanged: boolean;
  priceChanged: boolean;
  originalKcal: number;
  calculatedKcal: number;
  currentWeight: number;
  newWeight: number;
  currentActivity: string;
  newActivity: string;
  currentBody: string;
  newBody: string;
  currentSnackLevel: string;
  newSnackLevel: string;
  oldPrice: number;
  newPrice: number;
  currencyCode: string;
  discount: any;
}

const ChangesSummary: React.FC<ChangesSummaryProps> = ({
  weightChanged,
  activityChanged,
  bodyChanged,
  snackLevelChanged,
  caloriesChanged,
  priceChanged,
  originalKcal,
  calculatedKcal,
  currentWeight,
  newWeight,
  currentActivity,
  newActivity,
  currentBody,
  newBody,
  currentSnackLevel,
  newSnackLevel,
  oldPrice,
  newPrice,
  currencyCode,
  discount,
}) => {
  const intl = useIntl();
  const hasChanges =
    weightChanged ||
    activityChanged ||
    bodyChanged ||
    snackLevelChanged ||
    caloriesChanged ||
    priceChanged;
  if (!hasChanges) return null;

  return (
    <div className="product-preview-step__section">
      <label className="product-preview-step__label">
        <CheckCircleIcon />
        <FormattedMessage id="PET_PROFILE.YOUR_CHANGES" />
      </label>
      <div className="ppu-changes-list">
        <div className="ppu-change-item">
          <div className="ppu-change-item__icon">
            <FireIcon />
          </div>
          <div className="ppu-change-item__content">
            <span className="ppu-change-item__label">
              <FormattedMessage id="PET_PROFILE.LABEL.INDIVIDUAL_CALORIE_NEED" />
            </span>
            <div className="ppu-change-item__values">
              {caloriesChanged && originalKcal > 0 && (
                <>
                  <span className="ppu-change-item__old">
                    {intl.formatMessage({ id: 'KCAL.PLAIN' }, { kcal: originalKcal })}
                  </span>
                  <span className="ppu-change-item__arrow">→</span>
                </>
              )}
              <span className="ppu-change-item__new">
                {intl.formatMessage({ id: 'KCAL.PER_DAY_LONG' }, { kcal: calculatedKcal })}
              </span>
            </div>
          </div>
        </div>

        {weightChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <ScaleIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.WEIGHT" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">
                  {intl.formatMessage({ id: 'PET.WEIGHT_KG' }, { weight: currentWeight })}
                </span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">
                  {intl.formatMessage({ id: 'PET.WEIGHT_KG' }, { weight: newWeight })}
                </span>
              </div>
            </div>
          </div>
        )}

        {activityChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <LightningIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.ACTIVITY" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">{currentActivity}</span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">{newActivity}</span>
              </div>
            </div>
          </div>
        )}

        {bodyChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <HeartIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.BODY_SHAPE" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">{currentBody}</span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">{newBody}</span>
              </div>
            </div>
          </div>
        )}

        {snackLevelChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
                <circle cx="12" cy="14" r="1" fill="currentColor" />
              </svg>
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="PET.SNACKS_TREATS" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">{currentSnackLevel}</span>
                <span className="ppu-change-item__arrow">→</span>
                <span className="ppu-change-item__new">{newSnackLevel}</span>
              </div>
            </div>
          </div>
        )}

        {priceChanged && (
          <div className="ppu-change-item">
            <div className="ppu-change-item__icon">
              <PriceIcon />
            </div>
            <div className="ppu-change-item__content">
              <span className="ppu-change-item__label">
                <FormattedMessage id="GLOBAL.PRICE" />
              </span>
              <div className="ppu-change-item__values">
                <span className="ppu-change-item__old">
                  {formatMoney(oldPrice, currencyCode, intl.locale)}
                </span>
                <span className="ppu-change-item__arrow">→</span>
                <span
                  className={`ppu-change-item__new ${
                    newPrice > oldPrice
                      ? 'ppu-change-item__new--increase'
                      : 'ppu-change-item__new--decrease'
                  }`}>
                  {formatMoney(newPrice, currencyCode, intl.locale)}
                </span>
              </div>
              {discount && (
                <span className="ppu-change-item__discount">
                  <FormattedMessage
                    id="DISCOUNT.APPLIED"
                    values={{
                      label:
                        formatDiscountTitle(intl, discount.title) ||
                        intl.formatMessage({ id: 'DISCOUNT.FALLBACK' }),
                    }}
                  />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface RecipeSelectorProps {
  selectedRecipes: Recipe[];
  disabledRecipes: string[];
  petName?: string;
  onToggle: (recipe: Recipe) => void;
}

const RecipeSelector: React.FC<RecipeSelectorProps> = ({
  selectedRecipes,
  disabledRecipes,
  petName,
  onToggle,
}) => {
  const intl = useIntl();

  return (
    <div className="ppu-product-details__group">
      <span className="ppu-product-details__label">
        {intl.formatMessage({ id: 'PET_PROFILE.LABEL.RECIPES' })}
      </span>
      <div className="ppu-recipe-list">
        {Object.values(RECIPE_INFO)
          .filter((info) => !isHiddenRecipe(info.id))
          .map((info) => {
            const recipe = info.id;
            const isSelected = selectedRecipes.includes(recipe);
            const isDisabled = disabledRecipes.includes(recipe);

            return (
              <div
                key={recipe}
                onClick={() => onToggle(recipe)}
                className={`ppu-recipe-item ppu-recipe-item--selectable ${
                  isSelected ? 'ppu-recipe-item--selected' : ''
                } ${isDisabled ? 'ppu-recipe-item--disabled' : ''}`}>
                <img
                  src={toAbsoluteUrl(info.icon)}
                  alt={info.name}
                  className="ppu-recipe-item__image"
                />
                <div className="ppu-recipe-item__info">
                  <span className="ppu-recipe-item__name">
                    {intl.formatMessage(
                      { id: 'SUBSCRIPTION.FEEDING.FRESH_WITH' },
                      { meat: intl.formatMessage({ id: info.meatId }) },
                    )}
                  </span>
                  <span className="ppu-recipe-item__protein">
                    {isDisabled
                      ? intl.formatMessage(
                          { id: 'PET_PROFILE.RECIPE.IS_ALLERGIC' },
                          { name: petName },
                        )
                      : info.protein}
                  </span>
                </div>
                {isSelected && (
                  <div className="ppu-recipe-item__check">
                    <CheckIcon />
                  </div>
                )}
                {isDisabled && (
                  <span className="ppu-recipe-item__warning">
                    <WarningIcon />
                  </span>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

interface PensionSelectorProps {
  selectedPension: Pension;
  selectedRecipes: Recipe[];
  selectedCycle: Cycle;
  variants: any[] | undefined;
  disabledPensions?: Pension[];
  onChange: (pension: Pension) => void;
}

const PensionSelector: React.FC<PensionSelectorProps> = ({
  selectedPension,
  selectedRecipes,
  selectedCycle,
  variants,
  disabledPensions = [],
  onChange,
}) => {
  const intl = useIntl();

  return (
    <div className="ppu-product-details__group">
      <span className="ppu-product-details__label">
        {intl.formatMessage({ id: 'SUBSCRIPTION.PORTION_SIZE' })}
      </span>
      <div className="ppu-selection-grid-2">
        {Object.entries(PENSION_INFO).map(([key, info]) => {
          const isAvailable = variants
            ? hasMatchingVariant(variants, {
                recipes: selectedRecipes,
                pension: key as Pension,
                cycle: selectedCycle,
              })
            : false;
          const isDisabledByRule = disabledPensions.includes(key as Pension);
          const isEnabled = isAvailable && !isDisabledByRule;

          return (
            <div
              key={key}
              onClick={() => isEnabled && onChange(key as Pension)}
              className={`ppu-selection-item ${
                selectedPension === key ? 'ppu-selection-item--selected' : ''
              } ${!isEnabled ? 'ppu-selection-item--disabled' : ''}`}>
              <div className="ppu-selection-item__content">
                <span className="ppu-selection-item__label">
                  {intl.formatMessage({ id: info.labelId })}
                </span>
                <span className="ppu-selection-item__sublabel">
                  {isEnabled
                    ? intl.formatMessage({ id: info.descriptionId })
                    : isDisabledByRule
                      ? intl.formatMessage({ id: 'PET_PROFILE.PENSION.UNAVAILABLE_MANY_SNACKS' })
                      : intl.formatMessage({ id: 'PET_PROFILE.PENSION.UNAVAILABLE_RECIPES' })}
                </span>
              </div>
              {selectedPension === key && (
                <div className="ppu-recipe-item__check">
                  <CheckIcon />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface CycleSelectorProps {
  selectedCycle: Cycle;
  selectedRecipes: Recipe[];
  selectedPension: Pension;
  variants: any[] | undefined;
  onChange: (cycle: Cycle) => void;
}

const CycleSelector: React.FC<CycleSelectorProps> = ({
  selectedCycle,
  selectedRecipes,
  selectedPension,
  variants,
  onChange,
}) => {
  const intl = useIntl();

  // Distinct cycles the recalculated product offers, shortest first to match the
  // delivery-plan cards in `cycles/Cycles.tsx`. Derived from the variants instead
  // of a hardcoded [28, 14] so new selling plans (21/35/42/56/…) all render; each
  // card is only enabled when a variant exists for the current recipes + pension.
  const cycles = React.useMemo(() => {
    const set = new Set<number>();
    (variants || []).forEach((variant) => {
      const cycle = variant.selectedOptions?.cycle;
      if (typeof cycle === 'number' && Number.isFinite(cycle)) set.add(cycle);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [variants]);

  return (
    <div className="ppu-product-details__group">
      <span className="ppu-product-details__label">
        {intl.formatMessage({ id: 'SUBSCRIPTION.UPDATE.LABEL_DELIVERY_PLAN' })}
      </span>
      <div className="ppu-selection-grid-2">
        {cycles.map((cycleKey) => {
          const info = getCycleInfo(intl, cycleKey);
          const matchingVariant = variants
            ? findMatchingVariant(variants, {
                recipes: selectedRecipes,
                pension: selectedPension,
                cycle: cycleKey,
              })
            : null;
          // Offer the cycle only when a matching variant exists AND isn't
          // explicitly flagged not-for-sale. A missing flag is treated as
          // available (conservative), matching getIsCurrentPlanUnavailableSelector.
          const isAvailable = !!matchingVariant && !isVariantExplicitlyNotForSale(matchingVariant);

          return (
            <div
              key={cycleKey}
              onClick={() => isAvailable && onChange(cycleKey)}
              className={`ppu-selection-item ${
                selectedCycle === cycleKey ? 'ppu-selection-item--selected' : ''
              } ${!isAvailable ? 'ppu-selection-item--disabled' : ''}`}>
              <div className="ppu-selection-item__content">
                <span className="ppu-selection-item__label">{info.label}</span>
                <span className="ppu-selection-item__sublabel">
                  {isAvailable
                    ? info.description
                    : intl.formatMessage({ id: 'PET_PROFILE.CYCLE.UNAVAILABLE_SELECTION' })}
                </span>
              </div>
              {selectedCycle === cycleKey && (
                <div className="ppu-recipe-item__check">
                  <CheckIcon />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface PriceDisplayProps {
  newPrice: number;
  currencyCode: string;
  discount: any;
}

// A file-local duplicate of components/PriceDisplay.tsx. Both are live, so both are
// translated; they are not merged here because that is a refactor, not a translation.
const PriceDisplay: React.FC<PriceDisplayProps> = ({ newPrice, currencyCode, discount }) => {
  const intl = useIntl();

  return (
    <div className="product-preview-step__section">
      <label className="product-preview-step__label">
        <PriceIcon />
        <FormattedMessage id="GLOBAL.PRICE" />
      </label>
      <div className="ppu-price-display">
        <div className="ppu-price-display__icon">
          <PriceIcon />
        </div>
        <div className="ppu-price-display__content">
          <span className="ppu-price-display__label">
            <FormattedMessage id="PET_PROFILE.PRICE.NEW_PER_DELIVERY" />
          </span>
          <div className="ppu-price-display__values">
            <span className="ppu-price-display__value">
              {formatMoney(newPrice, currencyCode, intl.locale)}
            </span>
            <span className="ppu-price-display__note">
              <FormattedMessage id="GLOBAL.INCL_VAT" />
            </span>
          </div>
          {discount && (
            <span className="ppu-price-display__discount">
              <FormattedMessage
                id="DISCOUNT.APPLIED"
                values={{
                  label:
                    formatDiscountTitle(intl, discount.title) ||
                    intl.formatMessage({ id: 'DISCOUNT.FALLBACK' }),
                }}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const RecipeCustomizationStep: React.FC = () => {
  const {
    mode,
    petInfo,
    customization,
    setCustomization,
    recalculatedProduct,
    selectedVariant,
    currentPetInfo,
    currentOptions,
    goBack,
    handleClose,
    isRecalculating,
    isUpdating,
    handleSubmit,
  } = usePetProfileUpdateContext();

  const intl = useIntl();
  const pet = useSelector(getPetSelector);
  // Hoisted rather than called in the JSX: the title is rendered from two
  // alternative return branches, and a hook belongs at the top level of the
  // component regardless of which branch runs.
  const possessiveName = usePossessiveName(pet?.name);
  const lineItem = useSelector(getLineItemSelector);
  // Only subscription-level promo codes affect the fresh food price shown here.
  // Item-scoped redemptions (Pfotenpunkte / Sommer Aktion) belong to shop line
  // items, not this plan, so they're excluded.
  const subscriptionDiscounts = useSelector(getSubscriptionLevelDiscountsSelector);
  const discount = subscriptionDiscounts[0];
  const currencyCode = useSelector(getCurrencyCodeSelector);
  const lastOrder = useSelector(getLastOrderSelector);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);

  const variants = recalculatedProduct?.product.variants;

  const calculatedKcal = recalculatedProduct?.calculatedCalories.kcal || 0;
  const originalKcal = lineItem?.product?.calories?.kcal
    ? typeof lineItem.product.calories.kcal === 'string'
      ? parseInt(lineItem.product.calories.kcal, 10)
      : lineItem.product.calories.kcal
    : 0;

  const newBasePrice = selectedVariant ? parseFloat(String(selectedVariant.price || '0')) : 0;
  const oldBasePrice = lineItem?.basePrice || 0;
  const oldDiscountedPrice = lineItem?.discountedPrice || oldBasePrice;

  const discountPercentage = useMemo<number | null>(() => {
    if (oldBasePrice > 0 && oldDiscountedPrice < oldBasePrice) {
      return ((oldBasePrice - oldDiscountedPrice) / oldBasePrice) * 100;
    }
    if (
      discount?.value &&
      typeof discount.value === 'object' &&
      'percentage' in discount.value &&
      typeof discount.value.percentage === 'number'
    ) {
      return discount.value.percentage;
    }
    return null;
  }, [oldBasePrice, oldDiscountedPrice, discount]);

  const newPrice =
    discountPercentage && discountPercentage > 0
      ? newBasePrice - (newBasePrice * discountPercentage) / 100
      : newBasePrice;
  const oldPrice = oldDiscountedPrice;

  const disabledRecipes = useMemo(() => {
    const allergyDisabled = pet ? getDisabledRecipes(pet) : [];
    const recalculatedDisabled = recalculatedProduct?.restrictions.disabledRecipes || [];
    return Array.from(new Set([...allergyDisabled, ...recalculatedDisabled]));
  }, [pet, recalculatedProduct]);

  const hasRestrictions = customization.selectedRecipes.some((r) => disabledRecipes.includes(r));

  // Determine if the current order is in the delivery process
  const orderInDelivery = Boolean(lastOrder);
  const nextDeliveryDate = useMemo(() => {
    if (!orderInDelivery || !defaultSubscription?.nextBillingDate) return null;
    // intl.formatDate rather than toLocaleDateString('de-AT'): this needs to be a
    // string for the surrounding copy, and the day-first order the hardcoded
    // Austrian locale produced is wrong for an English reader.
    return intl.formatDate(new Date(defaultSubscription.nextBillingDate), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, [intl, orderInDelivery, defaultSubscription?.nextBillingDate]);

  // The customer's current delivery cycle (in days) before recalculation.
  const currentCycle = currentOptions.cycle;

  // Detect which options are not available in the recalculated product.
  // Generalized from the old hardcoded "no 14-day cycle" check: true when the
  // customer's *current* cycle is no longer offered, so they must pick a new
  // delivery plan (e.g. a legacy 14/28 plan migrating to 42/56).
  const currentCycleUnavailable = useMemo(() => {
    if (!variants || variants.length === 0) return false;
    return !variants.some((v) => v.selectedOptions?.cycle === currentCycle);
  }, [variants, currentCycle]);

  // Halbpension can be unavailable for two independent reasons:
  //  1) the recalculated calorie need has no Halbpension variant (calorie-driven), or
  //  2) the dog eats lots of snacks (business rule, see snackForcesVoll below).
  // This flag captures ONLY the calorie-driven case; the "Angepasste
  // Lieferoptionen" alert speaks to calories, while the snack-driven case is
  // communicated directly on the pension selector ("Bei viel Snacks nicht
  // verfügbar").
  const noHalbpensionVariant = useMemo(() => {
    if (!variants || variants.length === 0) return false;
    return !variants.some((v) => v.selectedOptions?.pension === 'H');
  }, [variants]);

  const hasReducedOptions = currentCycleUnavailable || noHalbpensionVariant;

  // When the dog eats lots of snacks, Halbpension is not allowed: the plan must
  // stay on Vollpension so the (nutritionally complete) food remains the main
  // part of the diet. Used by every pension-selection path below.
  const snackForcesVoll = petInfo.snackLevel === 'lots';

  // Edge case: the snack rule forces Vollpension, but the recalculated calorie
  // tier has no Vollpension variant for the selected recipes (at any cycle). The
  // combination is impossible, so we block saving and guide the user to fix it.
  const snackConflict = useMemo(() => {
    if (!snackForcesVoll || !variants || variants.length === 0) return false;
    return !hasMatchingVariant(variants, {
      recipes: customization.selectedRecipes,
      pension: 'V',
    });
  }, [snackForcesVoll, variants, customization.selectedRecipes]);

  const weightChanged = petInfo.weight !== currentPetInfo.weight;
  const activityChanged = petInfo.activity !== currentPetInfo.activity;
  const bodyChanged = petInfo.body !== currentPetInfo.body;
  const snackLevelChanged = petInfo.snackLevel !== currentPetInfo.snackLevel;
  const caloriesChanged = calculatedKcal !== originalKcal && originalKcal > 0;
  const priceChanged = !!selectedVariant && Math.abs(newPrice - oldPrice) > 0.01 && oldPrice > 0;

  // The resolved variant can still be flagged not-for-sale. Block saving onto a
  // dead plan and prompt the user to pick a different delivery cycle. A missing
  // flag is treated as available (conservative).
  const selectedVariantUnavailable =
    !!selectedVariant && isVariantExplicitlyNotForSale(selectedVariant);

  const handleRecipeToggle = (recipe: Recipe) => {
    const isSelected = customization.selectedRecipes.includes(recipe);
    const isDisabled = disabledRecipes.includes(recipe);

    if (isDisabled && !isSelected) return;

    let newRecipes: Recipe[];
    if (isSelected) {
      if (customization.selectedRecipes.length <= 1) return;
      newRecipes = customization.selectedRecipes.filter((r) => r !== recipe);
    } else {
      newRecipes = [...customization.selectedRecipes, recipe];
    }

    let nextPension = customization.pension;
    if (
      variants &&
      !hasMatchingVariant(variants, {
        recipes: newRecipes,
        pension: customization.pension,
        cycle: customization.cycle,
      })
    ) {
      // Prefer Vollpension; only allow a Halbpension fallback when snacks permit.
      const preferredPensions: Pension[] = snackForcesVoll ? ['V'] : ['V', 'H'];
      const fallback =
        preferredPensions
          .map((p) => findMatchingVariant(variants, { recipes: newRecipes, pension: p }))
          .find(Boolean) || findMatchingVariant(variants, { recipes: newRecipes });
      if (fallback) {
        nextPension = fallback.selectedOptions.pension;
      }
    }

    setCustomization({
      ...customization,
      selectedRecipes: newRecipes,
      pension: nextPension,
    });
  };

  const handlePensionChange = (pension: Pension) => {
    if (snackForcesVoll && pension === 'H') return;
    if (
      variants &&
      !hasMatchingVariant(variants, {
        recipes: customization.selectedRecipes,
        pension,
        cycle: customization.cycle,
      })
    )
      return;
    setCustomization({ ...customization, pension });
  };

  const handleCycleChange = (cycle: Cycle) => {
    if (
      variants &&
      !hasMatchingVariant(variants, {
        recipes: customization.selectedRecipes,
        pension: customization.pension,
        cycle,
      })
    )
      return;
    setCustomization({ ...customization, cycle });
  };

  useEffect(() => {
    if (!recalculatedProduct || !variants) return;

    const { selectedRecipes, pension, cycle } = customization;

    // 1) Snacks forbid Halbpension: always move off it first. If no Vollpension
    // variant exists this still pins us to 'V', which yields a null variant and
    // surfaces the snack-conflict message below (rather than silently keeping
    // a Halbpension plan).
    if (snackForcesVoll && pension === 'H') {
      setCustomization((prev) => ({ ...prev, pension: 'V' as Pension }));
      return;
    }

    // 2) Current selection already maps to a real variant — nothing to correct.
    if (hasMatchingVariant(variants, { recipes: selectedRecipes, pension, cycle })) return;

    // 3) Pick a fallback. Prefer Vollpension; only allow Halbpension when the
    // snack level permits it.
    const preferredPensions: Pension[] = snackForcesVoll ? ['V'] : ['V', 'H'];

    // Keep the same cycle when possible.
    for (const p of preferredPensions) {
      if (hasMatchingVariant(variants, { recipes: selectedRecipes, pension: p, cycle })) {
        setCustomization((prev) => ({ ...prev, pension: p }));
        return;
      }
    }

    // Otherwise allow a different cycle, still preferring Vollpension.
    for (const p of preferredPensions) {
      const match = findMatchingVariant(variants, { recipes: selectedRecipes, pension: p });
      if (match) {
        setCustomization((prev) => ({
          ...prev,
          pension: p,
          cycle: match.selectedOptions.cycle as Cycle,
        }));
        return;
      }
    }

    // 4) No preferred variant exists for these recipes at any cycle.
    if (snackForcesVoll) {
      // Lots of snacks MUST stay on Vollpension. With no Vollpension variant the
      // combination is impossible: pin to 'V' (no resolved variant) so the UI
      // blocks and shows the snack-conflict message instead of downgrading to
      // Halbpension. We're already on 'V' here (step 1 moved us off 'H').
      if (pension !== 'V') {
        setCustomization((prev) => ({ ...prev, pension: 'V' as Pension }));
      }
      return;
    }

    // Last resort (snacks allow Halbpension): accept whatever the catalog offers
    // so the user still has a valid product rather than a dead-end state.
    const anyVariant = findMatchingVariant(variants, { recipes: selectedRecipes });
    if (anyVariant) {
      setCustomization((prev) => ({
        ...prev,
        pension: anyVariant.selectedOptions.pension as Pension,
        cycle: anyVariant.selectedOptions.cycle as Cycle,
      }));
    }
  }, [
    customization.selectedRecipes,
    customization.pension,
    customization.cycle,
    recalculatedProduct,
    variants,
    snackForcesVoll,
    setCustomization,
  ]);

  if (isRecalculating) {
    return (
      <div className="product-preview-step ">
        <div className="product-preview-step__header opacity-0">
          <h3>
            <FormattedMessage
              id="PET_PROFILE.RECIPE_STEP.TITLE"
              values={{ name: possessiveName }}
            />
          </h3>
          <p>
            <FormattedMessage id="PET_PROFILE.RECIPE_STEP.SUBTITLE" />
          </p>
        </div>
        <div className="product-preview-step__container product-preview-step__container--loading">
          <div className="ppu-loading">
            <div className="ppu-loading__spinner">
              <LoadingSpinner />
            </div>
            <p className="ppu-loading__title">
              <FormattedMessage id="PET_PROFILE.CALCULATING" />
            </p>
            <p className="ppu-loading__subtitle">
              <FormattedMessage id="GLOBAL.PLEASE_WAIT" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-preview-step">
      <div className="product-preview-step__header">
        <h3>
          <FormattedMessage id="PET_PROFILE.RECIPE_STEP.TITLE" values={{ name: possessiveName }} />
        </h3>
        <p>
          <FormattedMessage id="PET_PROFILE.RECIPE_STEP.SUBTITLE" />
        </p>
      </div>

      {orderInDelivery && nextDeliveryDate && (
        <div className="calorie-gauge__pet-msg-content" style={{ marginBottom: '1.5rem' }}>
          <KTSVG path={toAbsoluteUrl('/media/svg/general/orange-info-icon.svg')} />
          <p>
            <FormattedMessage
              id="SUBSCRIPTION.UPDATE.ORDER_IN_PROGRESS"
              values={{ date: nextDeliveryDate, strong: (c) => <strong>{c}</strong> }}
            />
          </p>
        </div>
      )}

      <div className="product-preview-step__container">
        <div className="product-preview-step__main">
          <ChangesSummary
            weightChanged={weightChanged}
            activityChanged={activityChanged}
            bodyChanged={bodyChanged}
            snackLevelChanged={snackLevelChanged}
            caloriesChanged={caloriesChanged}
            priceChanged={priceChanged}
            originalKcal={originalKcal}
            calculatedKcal={calculatedKcal}
            currentWeight={currentPetInfo.weight}
            newWeight={petInfo.weight}
            currentActivity={intl.formatMessage({
              id: ACTIVITY_INFO[currentPetInfo.activity].labelId,
            })}
            newActivity={intl.formatMessage({ id: ACTIVITY_INFO[petInfo.activity].labelId })}
            currentBody={intl.formatMessage({ id: BODY_INFO[currentPetInfo.body].labelId })}
            newBody={intl.formatMessage({ id: BODY_INFO[petInfo.body].labelId })}
            currentSnackLevel={intl.formatMessage({
              id: SNACK_LEVEL_INFO[currentPetInfo.snackLevel].labelId,
            })}
            newSnackLevel={intl.formatMessage({
              id: SNACK_LEVEL_INFO[petInfo.snackLevel].labelId,
            })}
            oldPrice={oldPrice}
            newPrice={newPrice}
            currencyCode={currencyCode}
            discount={discount}
          />
        </div>

        <div className="product-preview-step__sidebar">
          <div className="product-preview-step__section">
            <label className="product-preview-step__label">
              <PackageIcon />
              <FormattedMessage id="SUBSCRIPTION.PENSION.PRODUCT_DETAILS" />
            </label>
            <div className="ppu-product-details">
              <RecipeSelector
                selectedRecipes={customization.selectedRecipes}
                disabledRecipes={disabledRecipes}
                petName={pet?.name}
                onToggle={handleRecipeToggle}
              />
              <PensionSelector
                selectedPension={customization.pension}
                selectedRecipes={customization.selectedRecipes}
                selectedCycle={customization.cycle}
                variants={variants}
                disabledPensions={petInfo.snackLevel === 'lots' ? (['H'] as Pension[]) : []}
                onChange={handlePensionChange}
              />
              <CycleSelector
                selectedCycle={customization.cycle}
                selectedRecipes={customization.selectedRecipes}
                selectedPension={customization.pension}
                variants={variants}
                onChange={handleCycleChange}
              />
              {hasReducedOptions && (
                <div className="ppu-alert" style={{ marginTop: '1rem' }}>
                  <div className="ppu-alert__icon">
                    <img
                      src={toAbsoluteUrl('/media/svg/general/info.svg')}
                      alt="Info"
                      style={{ width: '16px', height: '16px' }}
                    />
                  </div>
                  <div className="ppu-alert__content">
                    <p className="ppu-alert__title">
                      <FormattedMessage id="PET_PROFILE.REDUCED.TITLE" />
                    </p>
                    <p className="ppu-alert__text">
                      {intl.formatMessage(
                        {
                          id:
                            currentCycleUnavailable && noHalbpensionVariant
                              ? 'PET_PROFILE.REDUCED.CYCLE_AND_HALF'
                              : currentCycleUnavailable
                                ? 'PET_PROFILE.REDUCED.CYCLE'
                                : 'PET_PROFILE.REDUCED.HALF',
                        },
                        {
                          kcal: calculatedKcal,
                          cycle: formatCycleInterval(intl, currentCycle),
                          name:
                            pet?.name ||
                            intl.formatMessage({ id: 'PET_PROFILE.YOUR_DOG_FALLBACK_ACC' }),
                        },
                      )}
                    </p>
                    {newPrice > oldPrice && oldPrice > 0 && (
                      <p className="ppu-alert__text">
                        <FormattedMessage
                          id="PET_PROFILE.PRICE.CHANGE_NOTE"
                          values={{
                            oldPrice: formatMoney(oldPrice, currencyCode, intl.locale),
                            newPrice: formatMoney(newPrice, currencyCode, intl.locale),
                          }}
                        />
                      </p>
                    )}
                  </div>
                </div>
              )}
              {selectedVariant?.metafields?.product_info?.sachets && (
                <div className="ppu-product-details__group">
                  <span className="ppu-product-details__label">
                    {intl.formatMessage({ id: 'PET_PROFILE.LABEL.YOUR_DELIVERY' })}
                  </span>
                  <ul className="ppu-sachets-list">
                    {selectedVariant.metafields.product_info.sachets
                      .reduce<
                        {
                          type: string;
                          serve: number;
                          pouches: { quantity: number; value: number; unit: string }[];
                        }[]
                      >((acc, sachet) => {
                        // A recipe can span two pouch slots with different
                        // grammages (e.g. GS as 800g + 600g). They share the
                        // same serve, so group them into one row per recipe and
                        // join the pouch sizes instead of duplicating the row.
                        if (!sachet.quantity) return acc;
                        const pouch = {
                          quantity: sachet.quantity,
                          value: sachet.weight.value,
                          unit: sachet.weight.unit,
                        };
                        const existing = acc.find((group) => group.type === sachet.type);
                        if (existing) {
                          existing.pouches.push(pouch);
                        } else {
                          acc.push({ type: sachet.type, serve: sachet.serve, pouches: [pouch] });
                        }
                        return acc;
                      }, [])
                      .map((group) => {
                        const sachetTypeText =
                          RECIPE_INFO[group.type as Recipe]?.protein || group.type;
                        const quantityText = group.pouches
                          .map((pouch) => `${pouch.quantity} x ${pouch.value}${pouch.unit}`)
                          .join(', ');
                        return (
                          <li className="ppu-sachets-list__item" key={group.type}>
                            <span className="ppu-sachets-list__quantity">{quantityText}</span>
                            <span className="ppu-sachets-list__name">{sachetTypeText}</span>
                            <span className="ppu-sachets-list__serve">
                              {intl.formatMessage(
                                { id: 'PET_PROFILE.SACHETS.LASTS_DAYS' },
                                { days: group.serve },
                              )}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {selectedVariant && !selectedVariantUnavailable && (
            <PriceDisplay newPrice={newPrice} currencyCode={currencyCode} discount={discount} />
          )}

          {selectedVariant && selectedVariantUnavailable && (
            <div className="product-preview-step__section">
              <div className="ppu-alert ppu-alert--error">
                <div className="ppu-alert__icon">
                  <ErrorIcon />
                </div>
                <div className="ppu-alert__content">
                  <p className="ppu-alert__title">
                    <FormattedMessage id="PET_PROFILE.CYCLE_GONE.TITLE" />
                  </p>
                  <p className="ppu-alert__text">
                    <FormattedMessage id="PET_PROFILE.CYCLE_GONE.TEXT" />
                  </p>
                </div>
              </div>
            </div>
          )}

          {!selectedVariant && snackConflict && (
            <div className="product-preview-step__section">
              <div className="ppu-alert ppu-alert--error">
                <div className="ppu-alert__icon">
                  <ErrorIcon />
                </div>
                <div className="ppu-alert__content">
                  <p className="ppu-alert__title">
                    <FormattedMessage id="PET_PROFILE.SNACK_CONFLICT.TITLE" />
                  </p>
                  <p className="ppu-alert__text">
                    {intl.formatMessage(
                      { id: 'PET_PROFILE.SNACK_CONFLICT.TEXT' },
                      {
                        name:
                          pet?.name ||
                          intl.formatMessage({ id: 'PET_PROFILE.YOUR_DOG_FALLBACK_ACC' }),
                        kcal: calculatedKcal,
                      },
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!selectedVariant && !snackConflict && (
            <div className="product-preview-step__section">
              <div className="ppu-alert ppu-alert--error">
                <div className="ppu-alert__icon">
                  <ErrorIcon />
                </div>
                <div className="ppu-alert__content">
                  <p className="ppu-alert__title">
                    <FormattedMessage id="PET_PROFILE.RECIPE_DIFF.UNAVAILABLE_TITLE" />
                  </p>
                  <p className="ppu-alert__text">
                    <FormattedMessage id="PET_PROFILE.NO_PRODUCT_FOR_KCAL" />
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="product-preview-step__actions">
        <button
          type="button"
          onClick={mode === 'migration' ? handleClose : goBack}
          className="ppu-action-button ppu-action-button--secondary">
          <ArrowLeftIcon />
          <FormattedMessage id="GLOBAL.BACK" />
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={
            !selectedVariant ||
            selectedVariantUnavailable ||
            isUpdating ||
            hasRestrictions ||
            snackConflict
          }
          className={`ppu-action-button ppu-action-button--primary ${
            isUpdating ? 'ppu-action-button--loading' : ''
          }`}>
          {isUpdating ? (
            <>
              <LoadingSpinner />
              <FormattedMessage id="GLOBAL.SAVING" />
            </>
          ) : (
            <>
              <FormattedMessage id="GLOBAL.SAVE" />
              <CheckmarkIcon />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
